// Hook for user management operations
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  ResetPasswordRequest,
  InviteUserRequest,
} from '@/types/user'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:54321/functions/v1'
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

export function useUsers() {
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const callUserManagement = useCallback(async (action: string, body: any = {}) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_URL}/user-management`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': ANON_KEY,  // Required for Edge Functions
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, ...body }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Operation failed')
    }

    return data
  }, [])

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await callUserManagement('list_users')
      setUsers(data.users || [])
    } catch (err) {
      setError(err as Error)
      console.error('Failed to load users:', err)
    } finally {
      setIsLoading(false)
    }
  }, [callUserManagement])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const createUser = useCallback(async (userData: CreateUserRequest) => {
    try {
      await callUserManagement('create_user', userData)
      await loadUsers()
    } catch (err) {
      throw err
    }
  }, [callUserManagement, loadUsers])

  const updateUser = useCallback(async (updates: UpdateUserRequest) => {
    try {
      await callUserManagement('update_user', updates)
      await loadUsers()
    } catch (err) {
      throw err
    }
  }, [callUserManagement, loadUsers])

  const deleteUser = useCallback(async (userId: string) => {
    try {
      await callUserManagement('delete_user', { user_id: userId })
      await loadUsers()
    } catch (err) {
      throw err
    }
  }, [callUserManagement, loadUsers])

  const resetPassword = useCallback(async (request: ResetPasswordRequest) => {
    try {
      await callUserManagement('reset_password', request)
    } catch (err) {
      throw err
    }
  }, [callUserManagement])

  const inviteUser = useCallback(async (invitation: InviteUserRequest) => {
    try {
      const result = await callUserManagement('invite_user', invitation)
      return result
    } catch (err) {
      throw err
    }
  }, [callUserManagement])

  return {
    users,
    isLoading,
    error,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    resetPassword,
    inviteUser,
  }
}
